import { useMutation, InfiniteData, useQueryClient } from '@tanstack/react-query';
import { contentApi, IContentListResponse, TFolder, IContent } from '@_entities/content';
import { predicateType, predicateFolder } from '@_shared/libs';

export function useDeleteContentMutation(content: IContent) {
	const queryClient = useQueryClient();

	// delete Contents Mutation
	const { mutate: deleteContent } = useMutation({
		mutationFn: () => contentApi.remove(content.id),
		onMutate: async () => {
			// 해당 요청으로 온, 남아있던 요청이 있다면 취소해서 충돌 방지
			await queryClient.cancelQueries({
				predicate: (query) =>
					predicateFolder(query, String(content.folderId)) ||
					predicateFolder(query, 'all') ||
					predicateType(query, 'folderList'),
			});

			// 액션 전 데이터
			const previousContents = queryClient.getQueriesData<InfiniteData<IContentListResponse>>(
				{
					predicate: (query) =>
						predicateFolder(query, String(content.folderId)) ||
						predicateFolder(query, 'all'),
				},
			);
			const previousFolderList = queryClient.getQueriesData<TFolder[]>({
				predicate: (query) => predicateType(query, 'folderList'),
			});

			// 액션 : 폴더내 컨텐츠 리스트 낙관적 업데이트
			queryClient.setQueriesData<InfiniteData<IContentListResponse>>(
				{
					predicate: (query) =>
						predicateFolder(query, String(content.folderId)) ||
						predicateFolder(query, 'all'),
				},
				(old) => {
					if (!old) return old;

					return {
						...old,
						pages: old.pages.map((page) => ({
							...page,
							count: page.count - 1,
							contentData: page.contentData.filter(
								(pagedContent) => pagedContent.id !== content.id,
							),
							pinnedData: page.pinnedData?.filter(
								(pagedContent) => pagedContent.id !== content.id,
							),
						})),
					};
				},
			);

			// 액션: 폴더 리스트내 count 낙관적 업데이트
			queryClient.setQueriesData<TFolder[]>(
				{
					predicate: (query) => predicateType(query, 'folderList'),
				},
				(old) => {
					if (!old) return old;

					return old.map((folder) => {
						if (content.folderId === folder.id && folder.count) {
							return {
								...folder,
								count: folder.count - 1,
							};
						}

						return folder;
					});
				},
			);

			return { previousContents, previousFolderList };
		},
		onError: (_, __, context) => {
			context?.previousContents.forEach(([queryKey, previousData]) => {
				queryClient.setQueriesData<InfiniteData<IContentListResponse>>(
					{
						queryKey,
					},
					previousData,
				);
			});
			context?.previousFolderList.forEach(([queryKey, previousData]) => {
				queryClient.setQueriesData<TFolder[]>(
					{
						queryKey,
					},
					previousData,
				);
			});
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				predicate: (query) =>
					predicateFolder(query, String(content.folderId)) ||
					predicateFolder(query, 'all') ||
					predicateType(query, 'folderList'),
				refetchType: 'none',
			});
		},
	});

	return { deleteContent };
}
